import { Check as CheckIcon, ExpandMore as ExpandIcon } from "@mui/icons-material";
import { IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, TextField } from "@mui/material";
import type { FC } from "react";
import { useEffect, useRef, useState } from "react";
import "react-calendar/dist/Calendar.css";
import type { PriceValue } from "../../../misc/price";
import "./ProductOptionEdit.scss";

type InputMode = "NUMBER" | "ON_DEMAND" | "UNAVAILABLE";
const modes = [
  { value: "NUMBER", label: "Cena" },
  { value: "ON_DEMAND", label: "Na zapytanie" },
  { value: "UNAVAILABLE", label: "Niedostępny" },
] as const;

interface InputMenuProps {
  readonly mode: InputMode;
  readonly idSuffix?: string;
  readonly disabled?: boolean;
  readonly onChange: (mode: InputMode) => void;
}

const InputMenu: FC<InputMenuProps> = ({ mode, idSuffix, disabled, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChange = (mode: InputMode) => {
    onChange(mode);
    handleClose();
  };

  const buttonId = idSuffix ? `price-input-button-${idSuffix}` : "price-input-button";
  const menuId = idSuffix ? `day-input-menu-${idSuffix}` : "day-input-menu";
  return (
    <div>
      <IconButton
        id={buttonId}
        aria-controls={open ? menuId : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label="Rodzaj ceny"
        size="small"
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <ExpandIcon />
      </IconButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ list: { "aria-labelledby": buttonId } }}
      >
        {modes.map(({ value, label }) => (
          <MenuItem key={value} onClick={() => handleChange(value)}>
            {mode === value && (
              <ListItemIcon>
                <CheckIcon />
              </ListItemIcon>
            )}
            <ListItemText inset={mode !== value}>{label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

interface Props {
  readonly value: Exclude<PriceValue, "INVALID">;
  readonly label?: string;
  readonly testId?: string;
  readonly buttonIdSuffix?: string;
  readonly fullWidth?: boolean;
  readonly disabled?: boolean;
  readonly error?: string;
  onChange: (value: Exclude<PriceValue, "INVALID">) => void;
}

function toDisplayString(num: number): string {
  return String(num).replace(".", ",");
}

const PriceInput: FC<Props> = ({ value, label, testId, buttonIdSuffix, fullWidth, disabled, error, onChange }) => {
  const mode = typeof value === "number" ? "NUMBER" : value;
  const [rawText, setRawText] = useState<string>(mode === "NUMBER" ? toDisplayString(value as number) : "");
  const isFocusedRef = useRef(false);

  useEffect(() => {
    if (!isFocusedRef.current && mode === "NUMBER") {
      setRawText(toDisplayString(value as number));
    }
  }, [value, mode]);

  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    if (mode === "NUMBER") {
      const parsed = parseFloat(rawText.replace(",", "."));
      if (!isNaN(parsed)) {
        onChange(parsed);
        setRawText(toDisplayString(parsed));
      } else {
        setRawText(toDisplayString(value as number));
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setRawText(raw);
    const parsed = parseFloat(raw.replace(",", "."));
    if (!isNaN(parsed)) {
      onChange(parsed);
    }
  };

  const displayValue = mode === "NUMBER" ? rawText : (modes.find((m) => m.value === value)?.label ?? "");

  return (
    <TextField
      label={label}
      size="small"
      helperText={error ? error : null}
      error={!!error}
      type={mode === "NUMBER" ? "text" : undefined}
      value={displayValue}
      fullWidth={fullWidth}
      disabled={disabled}
      data-testid={testId}
      slotProps={{
        htmlInput: { inputMode: mode === "NUMBER" ? "decimal" : undefined },
        input: {
          endAdornment: (
            <InputAdornment position="end">
              <InputMenu
                idSuffix={buttonIdSuffix}
                mode={mode}
                disabled={disabled}
                onChange={(m) => onChange(m === "NUMBER" ? 0 : m)}
              />
            </InputAdornment>
          ),
          readOnly: mode !== "NUMBER",
        },
      }}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  );
};

export default PriceInput;
